import { Check, ArrowRight } from "lucide-react";
import { Reveal, SectionHeader } from "../Reveal";
import { FUNNEL_FORMATS } from "../../lib/data";
import { scrollToId } from "../../lib/scroll";

const WHY = [
    "Targeted market placement for your campaign",
    "Campaign setup & monitoring included",
    "Increased brand exposure across digital screens",
    "Flexible campaigns — from 2-day runs to extended campaigns",
    "Multi-location reach across markets and screens",
    "High-traffic environments: highways, airports, malls, transit, retail, hotels, offices & more",
];

export const FunnelServices = () => (
    <>
        <section id="services" className="relative bg-noir-surface py-24 sm:py-32 overflow-hidden" data-testid="funnel-services">
            <div className="absolute -top-24 left-1/4 h-[400px] w-[400px] rounded-full bg-hotpink/8 blur-[130px]" aria-hidden="true" />
            <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6">
                <SectionHeader
                    eyebrow="WHAT WE OFFER"
                    title={<>EVERYWHERE YOUR <span className="text-chrome-gradient">AUDIENCE LOOKS.</span></>}
                    sub="Thirteen digital advertising formats — pick one, or combine several to surround your audience."
                />
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
                    {FUNNEL_FORMATS.map((f, i) => (
                        <Reveal key={f.id} delay={(i % 4) * 0.06} className="h-full">
                            <article className="card-media group overflow-hidden h-full" data-testid={`service-card-${f.id}`}>
                                <div className="relative h-32 sm:h-40 overflow-hidden">
                                    <img src={f.img} alt={f.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/20 to-transparent" />
                                </div>
                                <div className="p-4 sm:p-5">
                                    <h3 className="font-alt text-[11px] sm:text-xs font-bold tracking-[0.1em] text-white leading-snug">{f.title}</h3>
                                    <p className="mt-2 hidden sm:block text-[12px] leading-relaxed text-zinc-400">{f.desc}</p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.1}>
                    <p className="mt-10 text-center font-alt text-[10px] tracking-[0.2em] text-zinc-500 uppercase" data-testid="services-availability-note">
                        Advertising formats and placements are subject to availability and confirmation.
                    </p>
                </Reveal>
            </div>
        </section>

        <section id="why" className="relative bg-white text-noir py-24 sm:py-32 overflow-hidden" data-testid="funnel-why">
            <div className="mx-auto max-w-[1200px] px-4 sm:px-6 grid lg:grid-cols-2 gap-12 items-center">
                <div>
                    <Reveal>
                        <p className="eyebrow">WHY IT MATTERS</p>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold leading-[1.03] tracking-tight">
                            WHY BUSINESSES ADVERTISE WITH <span className="text-[#A6A6A6]">WEALTHHAUS MEDIA.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <p className="mt-6 text-sm sm:text-base leading-relaxed text-zinc-600">
                            Digital billboard advertising puts your brand in front of more eyes — building visibility,
                            exposure, and local awareness in the places your audience already spends time.
                        </p>
                    </Reveal>
                    <Reveal delay={0.22}>
                        <button className="btn-glossy-pink mt-8" onClick={() => scrollToId("#pricing")} data-testid="why-pricing-button">
                            SEE CAMPAIGN OPTIONS <ArrowRight size={15} />
                        </button>
                    </Reveal>
                </div>
                <div className="space-y-3">
                    {WHY.map((w, i) => (
                        <Reveal key={w} delay={i * 0.06}>
                            <div className="flex items-start gap-3.5 rounded-xl border border-zinc-200 bg-white p-4.5 px-5 py-4 shadow-[0_8px_30px_rgba(8,7,11,0.06)] hover:border-noir/40 transition-colors" data-testid={`why-item-${i}`}>
                                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-noir/[0.06]">
                                    <Check size={13} className="text-noir" />
                                </span>
                                <p className="text-sm font-medium text-noir/85">{w}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    </>
);
